import { fromJSON } from "common/components/Dropdown/Dropdown";
import { IQueryParamsBaseRecord, defaultQueryParamsBase, toParams } from "common/records/index/QueryParamsBase";
import { Nullable } from "common/types";
import { List, Record } from "immutable";
import { isArray } from "underscore";
import { endOfYear, formatISO, sub, subMonths, subYears } from "date-fns";

export const jobParamsFromJSON = (data: IJobParamsData) => {
  const record = { ...(data as any) };

  if (isArray(data.state)) {
    record.state = List(data.state);
  }

  if (data.date_range !== "custom") {
    record.updated_since = calculateUpdatedSince(new JobParamsRecord(record));
    record.updated_before = calculateUpdatedBefore(new JobParamsRecord(record));
  } else {
    if (data.updated_since) {
      record.updated_since = new Date(data.updated_since + "T00:00:00");
    }
    if (data.updated_before) {
      record.updated_before = new Date(data.updated_before + "T23:59:59.999");
    }
  }

  return new JobParamsRecord(record);
};

export const toUrlSearchParams = (params: JobParamsRecord) => {
  const newParams: any = {};
  const jobParams = toParams(params);

  Object.keys(jobParams).forEach((key) => {
    if (jobParams[key] !== (params.clear().toJS() as any)[key]) {
      if (["updated_since", "updated_before"].includes(key)) {
        newParams[key] = formatISO((params.toJS() as any)[key], { representation: "date" });
      } else {
        newParams[key] = (params.toJS() as any)[key];
      }
    }
  });

  const newSearchParams = new URLSearchParams(newParams);
  if (params.get("date_range") !== "custom") {
    newSearchParams.delete("updated_since");
    newSearchParams.delete("updated_before");
  }
  return newSearchParams;
};

export const validateUpdatesDateRange = (_value: Date, rootRecord: any) => {
  if (rootRecord?.date_range !== "custom") {
    return null;
  }

  const limit = subYears(rootRecord.updated_before, 1);
  const invalidRange = rootRecord.updated_since < limit;
  if (invalidRange) {
    return "Date range should be less or equal to 12 months";
  }
  const invalidDateSelection = rootRecord.updated_before <= rootRecord.updated_since;
  if (invalidDateSelection) {
    return "End date cannot be before start date";
  }
  return null;
};

export interface IJobParamsData extends Partial<IQueryParamsBaseRecord> {
  org_id?: Nullable<number>;
  user_id?: Nullable<number>;
  state?: Nullable<string[]>;
  job_type?: Nullable<number>;
  lead_source?: Nullable<string>;
  date_range?: string;
  updated_since?: string;
  updated_before?: string;
  archived?: string;
}

export interface IJobParamsRecord extends IQueryParamsBaseRecord {
  org_id: Nullable<number>;
  user_id: Nullable<number>;
  state: Nullable<List<string>>;
  job_type: Nullable<number>;
  lead_source: Nullable<string>;
  date_range: string;
  updated_since: Date;
  updated_before: Date;
  archived: "false" | "true";
}

export const defaultJobParams: IJobParamsRecord = {
  ...defaultQueryParamsBase,
  sort_by: "jobs.updated_at",
  sort_order: "desc",
  org_id: null,
  user_id: null,
  state: null,
  job_type: null,
  lead_source: null,
  date_range: "6months",
  updated_since: subMonths(new Date().setHours(0, 0, 0, 0), 6),
  updated_before: subMonths(new Date(), 0),
  archived: "false",
};

export class JobParamsRecord extends Record(defaultJobParams) implements IJobParamsRecord {}

export const sortOrderDropdown = List([
  fromJSON({ id: "asc", name: "Oldest to Newest" }),
  fromJSON({ id: "desc", name: "Newest to Oldest" }),
]);

const year = new Date().getFullYear();
const years = [year, year - 1, year - 2, year - 3].map((year) => year.toString());

export const dateRangeDropdown = List([
  fromJSON({ id: "7days", name: "Last Week" }),
  fromJSON({ id: "14days", name: "Last 2 Weeks" }),
  fromJSON({ id: "1months", name: "Last Month" }),
  fromJSON({ id: "3months", name: "Last 3 Months" }),
  fromJSON({ id: "6months", name: "Last 6 Months" }),
  fromJSON({ id: "1years", name: "Last 12 Months" }),
  ...years.map((year) => fromJSON({ id: year, name: year })),
  fromJSON({ id: "custom", name: "Custom" }),
]);

export const calculateUpdatedSince = (jobParams: JobParamsRecord): Date => {
  const isYear = jobParams.date_range.length === Number.parseInt(jobParams.date_range).toString().length;

  if (isYear) {
    const year = parseInt(jobParams.date_range);
    const since = new Date(year, 0, 1, 0, 0, 0, 0);
    return since;
  } else if (jobParams.date_range !== "custom") {
    // It is a number of months / days
    const quantity = parseInt(jobParams.date_range);
    const unit = jobParams.date_range.slice(quantity.toString().length);
    const now = new Date();

    return sub(now, { [unit]: quantity });
  } else {
    return jobParams.updated_since;
  }
};

export const calculateUpdatedBefore = (jobParams: JobParamsRecord): Date => {
  const isYear = jobParams.date_range.length === Number.parseInt(jobParams.date_range).toString().length;

  if (isYear) {
    const year = parseInt(jobParams.date_range);
    const since = new Date(year, 0, 1, 0, 0, 0, 0);
    return endOfYear(since);
  } else if (jobParams.date_range !== "custom") {
    const today = new Date();
    return today;
  } else {
    return jobParams.updated_before;
  }
};
